import { cn } from '@/lib/utils';
import { stackBuckets, type ChartBucket } from '@/lib/issuingCharts';
import { ChartEmpty } from './ChartEmpty';
import type { ToneResolver } from './chartTones';

export interface ColumnBucket extends ChartBucket {
  /**
   * Tick text under the column, already formatted and translated. An empty
   * string renders no tick: ninety daily buckets cannot each carry a date, and
   * label density is the page's call, not a rule this component can invent.
   */
  readonly label: string;
}

interface StackedColumnChartProps {
  /** One column per bucket, in order. Buckets with no issuing stay in the list. */
  buckets: readonly ColumnBucket[];
  tone: ToneResolver;
  /** Renders the plot's ceiling value; supplied by the page, which knows the measure. */
  formatValue: (value: number) => string;
  /** Summary of the whole series — this chart has no text alternative besides it. */
  ariaLabel: string;
  emptyLabel: string;
  /**
   * What a faded column means, rendered under the axis and only when there is at
   * least one. Required rather than optional: a bucket can arrive marked partial
   * whatever the caller expected, and a column drawn differently with nothing
   * saying why is worse than one drawn the same as the rest.
   */
  partialNote: string;
  className?: string;
}

/**
 * One column per time bucket, segments stacked by category. Divs and
 * percentages: `lib/issuingCharts` decides every offset and height, this file
 * only places them.
 *
 * Accessibility: the drawing carries information that appears nowhere else as
 * text, so the plot is a labelled image (`role="img"`) and the caller's
 * `ariaLabel` is the alternative. The tick labels are inside it and so are not
 * announced separately — that is the trade the `role="img"` leaf makes, and it
 * beats announcing ninety unlabelled numbers.
 */
export function StackedColumnChart({
  buckets,
  tone,
  formatValue,
  ariaLabel,
  emptyLabel,
  partialNote,
  className,
}: StackedColumnChartProps) {
  if (buckets.length === 0) return <ChartEmpty label={emptyLabel} className={className} />;

  const { max, columns } = stackBuckets(buckets);
  const hasPartial = buckets.some((bucket) => bucket.partial);

  return (
    <div
      className={cn('min-w-0', className)}
      role="img"
      // The note is part of the alternative text, not decoration around it: a
      // reader who cannot see which columns are faded has to be told the series
      // contains some, or the summary overstates what the chart compares.
      aria-label={hasPartial ? `${ariaLabel}. ${partialNote}` : ariaLabel}
    >
      {/* The plot ceiling, stated — it is the only scale reference on the chart. */}
      <div className="mb-1 flex justify-end">
        <span className="font-mono text-[10.5px] tabular-nums text-muted-2">
          {formatValue(max)}
        </span>
      </div>

      <div className="relative flex h-40 items-stretch gap-[3px] border-b border-line">
        <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-line-2" />
        {columns.map((column, index) => (
          <div
            key={column.id}
            data-testid="column"
            // Same order as the input, which `stackBuckets` preserves, so the
            // bucket at this index is this column's.
            {...(buckets[index]?.partial ? { 'data-partial': 'true' } : {})}
            className={cn('relative min-w-0 flex-1', buckets[index]?.partial && 'opacity-50')}
          >
            {column.segments.map((segment) => (
              <div
                key={segment.id}
                data-testid="column-segment"
                className="absolute inset-x-0 rounded-[2px]"
                style={{
                  bottom: `${segment.offsetPct}%`,
                  height: `${segment.sizePct}%`,
                  background: tone(segment.id),
                }}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="mt-1.5 flex gap-[3px]">
        {buckets.map((bucket) => (
          <span
            key={bucket.id}
            className={cn(
              'min-w-0 flex-1 truncate text-center font-mono text-[9.5px] text-muted-2',
              bucket.partial && 'opacity-50',
            )}
          >
            {bucket.label}
          </span>
        ))}
      </div>

      {/* A partial column is shorter because it holds less time, which reads as
          a quiet week unless the chart says otherwise. */}
      {hasPartial && (
        <p data-testid="partial-note" className="mt-2 text-[10.5px] leading-snug text-muted-2">
          {partialNote}
        </p>
      )}
    </div>
  );
}
